import { CircularProgress } from '@material-ui/core';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useResponsiveValue } from '@superdispatch/ui';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { getBotpress, loadBotpress, showDefaultBotpressFab } from './Botpress';
import botpressIconUrl from './assets/botpress-icon.svg';

const BRAND_RED = '#FE4A49';
const BRAND_RED_DARK = '#E53E3D';

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const LauncherButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: #ffffff;
  background: linear-gradient(135deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%);
  cursor: pointer;
  overflow: hidden;
  animation: ${popIn} 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow:
    0 10px 24px -6px rgba(254, 74, 73, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.12);
  transition:
    transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 160ms cubic-bezier(0.2, 0.8, 0.2, 1);

  &:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow:
      0 16px 32px -8px rgba(254, 74, 73, 0.6),
      0 6px 12px rgba(0, 0, 0, 0.14);
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow:
      0 6px 14px -4px rgba(254, 74, 73, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.12);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px #ffffff,
      0 0 0 5px ${BRAND_RED},
      0 10px 24px -6px rgba(254, 74, 73, 0.45);
  }

  &:disabled {
    cursor: default;
  }

  & > img {
    transition: opacity 160ms ease;
  }

  &[aria-busy='true'] > img {
    opacity: 0;
  }
`;

const Spinner = styled(CircularProgress)`
  position: absolute;
  color: #ffffff;
`;

export function BotpressLauncher() {
  const isMobile = useResponsiveValue(true, true, false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const unsubscribers: Array<() => void> = [];

    void getBotpress().then((botpress) => {
      unsubscribers.push(botpress.on('webchat:opened', () => setIsOpen(true)));
      unsubscribers.push(botpress.on('webchat:closed', () => setIsOpen(false)));
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, []);

  if (isMobile || MobileAppBridge.isInjected() || isOpen) return null;

  async function handleClick() {
    showDefaultBotpressFab();
    setIsLoading(true);
    try {
      await loadBotpress();
      const botpress = await getBotpress();
      botpress.open();
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <LauncherButton
      type="button"
      disabled={isLoading}
      aria-label="Open support chat"
      aria-busy={isLoading}
      onClick={() => {
        void handleClick();
      }}
    >
      <img
        src={botpressIconUrl}
        alt=""
        width={64}
        height={64}
        aria-hidden="true"
      />
      {isLoading && <Spinner size={28} thickness={4} />}
    </LauncherButton>
  );
}
